import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import "../styles.css";

// List of Twitch usernames to feature - just add/remove usernames here
const FEATURED_STREAMERS = [
  "zemaxi",
  "krookediles",
  "rod_legion",
  "iminabearsuit",
  "morrowheat23",
  "enmita24",
  "avvetheoni",
  "PlayingCatchup",
  "schillerlocke",
  "lucindattv",
  "quickdex",
  "Necromaninon"
  // Add more Twitch usernames here
];

// Custom categories/descriptions (optional - falls back to Twitch bio)
const STREAMER_OVERRIDES = {
    //"zemaxi": { category: "Story Driven Gamer" },
    //"krookediles": { category: "Story Driven Gamer" },
    //"rod_legion": { category: "Story Driven Gamer" },
    //"PlayingCatchup": { category: "Trophy Hunter" },
    //"iminabearsuit": { category: "Retro and Horror Fanatic" },
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Streamers() {
  const [hoveredStreamer, setHoveredStreamer] = useState(null);
  const [streamers, setStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Format follower count (e.g., 1234 -> "1.2K")
  const formatFollowers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  useEffect(() => {
    const fetchStreamers = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/twitch/streamers?usernames=${FEATURED_STREAMERS.join(',')}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch streamer data');
        }
        
        const data = await response.json();
        
        // Merge API data with custom overrides
        const enrichedStreamers = data.streamers.map(streamer => ({
          ...streamer,
          category: STREAMER_OVERRIDES[streamer.twitchName]?.category || 'Variety Streamer',
          customDescription: STREAMER_OVERRIDES[streamer.twitchName]?.description
        }));
        
        // Sort: live streamers first, then keep original FEATURED_STREAMERS order
        enrichedStreamers.sort((a, b) => {
          if (a.isLive && !b.isLive) return -1;
          if (!a.isLive && b.isLive) return 1;
          // Keep original order from FEATURED_STREAMERS (case-insensitive)
          const aIndex = FEATURED_STREAMERS.findIndex(name => name.toLowerCase() === a.twitchName.toLowerCase());
          const bIndex = FEATURED_STREAMERS.findIndex(name => name.toLowerCase() === b.twitchName.toLowerCase());
          return aIndex - bIndex;
        });
        
        setStreamers(enrichedStreamers);
        setError(null);
      } catch (err) {
        console.error('Error fetching streamers:', err);
        setError('Unable to load streamer data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStreamers();
    
    // Refresh every 60 seconds to check for live status changes
    const interval = setInterval(fetchStreamers, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <SEO
        title="Featured Streamers"
        description="Discover awesome Twitch streamers recommended by ZeMaxi! Find new content creators to watch and support in the gaming community."
        keywords="Twitch streamers, gaming streamers, content creators, Twitch recommendations, gaming community"
        url="https://zemaxi.com/streamers"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="streamers-hero noise-bg">
          <div className="container">
            <h1>Featured <span className="highlight-gold">Streamers</span></h1>
            <p>Discover amazing content creators in our community! Show them some love!</p>
          </div>
        </section>
        <section className="streamers-section">
          <div className="container">
            {loading && (
              <div className="streamers-loading">
                <div className="spinner"></div>
                <p>Loading streamers...</p>
              </div>
            )}
            
            {error && (
              <div className="streamers-error">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{error}</p>
              </div>
            )}
            
            {!loading && !error && streamers.length === 0 && (
              <div className="streamers-empty">
                <i className="fab fa-twitch"></i>
                <p>No streamers found</p>
              </div>
            )}
            
            <div className="streamers-grid">
              {streamers.map((streamer, index) => (
                <a
                  href={`https://twitch.tv/${streamer.twitchName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`streamer-card-new ${hoveredStreamer === streamer.id ? 'hovered' : ''} ${streamer.isLive ? 'is-live' : ''}`}
                  key={streamer.id}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredStreamer(streamer.id)}
                  onMouseLeave={() => setHoveredStreamer(null)}
                >
                  <div className="streamer-card-glow"></div>
                  <div className="streamer-card-content">
                    <div className="streamer-avatar-wrapper">
                      <img
                        src={streamer.profilePic}
                        alt={`${streamer.displayName} profile`}
                        className="streamer-avatar"
                      />
                      <div className="avatar-ring"></div>
                      {streamer.isLive && <span className="live-badge">LIVE</span>}
                    </div>
                    <div className="streamer-info">
                      <h3 className="streamer-name">{streamer.displayName}</h3>
                      <span className="streamer-category">{streamer.category}</span>
                      {streamer.isLive && streamer.streamInfo && (
                        <div className="stream-info">
                          <span className="stream-game">{streamer.streamInfo.gameName}</span>
                          <span className="viewer-count">
                            <i className="fas fa-eye"></i> {streamer.streamInfo.viewerCount.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <p className="streamer-description">
                        {streamer.customDescription || streamer.description || 'Check out this awesome streamer!'}
                      </p>
                      <div className="twitch-button">
                        <i className="fab fa-twitch"></i> {streamer.isLive ? 'Watch Live!' : 'Watch on Twitch'}
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
